import { useEffect, useMemo, useRef, useState } from "react";
import { Columns3, Copy, Download, Filter as FilterIcon, History as HistoryIcon, ListPlus, Loader2, Plus, Search, SlidersHorizontal, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useColumnLayout, useHiddenColumns } from "@/hooks/useColumnLayout";
import { COLUMNS, cellValue, PqHistoryDialog, computePqPrice, PRICE_FORMULA_INPUTS_EXPORT as PRICE_FORMULA_INPUTS } from "@/pages/ProcurementQuotes";
import { pqChanges } from "@/lib/pqSync";
import { OtherCostDetailsDialog, sumOtherDetails } from "@/components/OtherCostDetailsDialog";

const num = (v) => parseFloat(v) || 0;

// procurement quote record -> price list row (matches PriceCompositionDialog formula fields)
function toPriceRow(r, importerName = "") {
  // 2026-06 · 其他1/2/3（FM 只读字段）也计入配件价，与采购报价的价格公式保持一致。
  const accessory = num(r.brand_paper) + num(r.plastic_bag) + num(r.liner_board) + num(r.carton_freight)
    + num(r.others) + num(r.others1) + num(r.others2) + num(r.others3);
  return {
    size: r.size || "",
    // 2026-06 · Source 采购报价 产品编号 — adopted as 采购产品编号 when empty.
    source_item_number: r.item_number || "",
    source_quote_id: r.id || "",
    // 2026-09 · 标注导入人（登录用户）与导入日期，显示在产品价格行上。
    source_buyer: importerName,
    imported_at: new Date().toISOString().slice(0, 10),
    factory_cost: num(r.price),
    cost_currency: "RMB",
    price_per_meter: num(r.price_per_meter),
    material_usage: num(r.material_size),
    processing_fee: num(r.processing_fee),
    accessory_price: Math.round(accessory * 100) / 100,
    profit_tax: num(r.profit_tax),
    loading_rate: num(r.packing_rate),
    qty_per_ctn: num(r.packing_rate),
    case_length: num(r.length),
    case_width: num(r.width),
    case_height: num(r.height),
    container_qty_40: num(r.qty_hq),
  };
}

// Fields that ProcurementQuoteCreate accepts on the backend — must send all of
// these on PUT so the pydantic model validates. Numeric fields default to 0.
const NUMERIC_FIELDS = new Set(["price", "price_per_meter", "qty_hq", "width", "length", "height"]);
const REQUIRED_FIELDS = ["item_number", "name_cn", "size", "factory", "price"];

function rowToPayload(r) {
  const p = {
    item_number: r.item_number || "",
    name_cn: r.name_cn || "",
    size: r.size || "",
    factory: r.factory || "",
    price: num(r.price),
    quote_customer: r.quote_customer || "",
    quote_date: r.quote_date || null,
    thickness: r.thickness || "",
    remark: r.remark || "",
    others: r.others || "",
    salesperson: r.salesperson || "",
    profit_tax: r.profit_tax || "",
    processing_fee: r.processing_fee || "",
    brand_paper: r.brand_paper || "",
    plastic_bag: r.plastic_bag || "",
    price_per_meter: num(r.price_per_meter),
    material_size: r.material_size || "",
    carton_freight: r.carton_freight || "",
    liner_board: r.liner_board || "",
    packing_rate: r.packing_rate || "",
    qty_hq: num(r.qty_hq),
    width: num(r.width),
    length: num(r.length),
    height: num(r.height),
    gross_weight: num(r.gross_weight),
    others_details: r.others_details || [],
    tag: r.tag || "",
  };
  return p;
}

// Procurement quote records dialog — opened from the product form's price
// list header. Original scope: pick FM-synced quotes that match this product's
// 采购产品编号 and import them as price rows.
//
// 2026-02 · Big usability upgrade requested by user:
//   1. 搜寻 — free-text search across ALL procurement quotes (including
//      other buyers' records), toggled from the header search box.
//   2. 复制 — leftmost per-row Copy button clones the record via
//      ``POST /procurement-quotes`` (source="web"). The clone becomes an
//      inline-editable row; blurring any cell auto-saves via PUT so nothing
//      needs an explicit "save" click.
//   3. 二级筛选 — secondary filter row (factory / salesperson / min price)
//      narrows the current result set client-side for fast triage.
//   4. Remark column widened ~40 % (320 px → 448 px) for legibility.
export function ProcurementHistoryDialog({ open, onOpenChange, buyerItemNumber, onImport }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const canCopy = user?.role === "admin" || user?.role === "buyer" || user?.role === "procurement_manager";

  // 2026-02-27 · A row is directly inline-editable (no need to click Copy
  // first) if the current user owns it OR is admin OR is a procurement
  // manager whose subordinate owns it.
  const subordinateNamesLower = (user?.subordinate_names_list || [])
    .map((n) => (n || "").trim().toLowerCase());
  const canEditRow = (r) => {
    if (user?.role === "admin") return true;
    if (user?.role === "procurement_manager") {
      const sp = (r.salesperson || "").trim().toLowerCase();
      return Boolean(sp) && subordinateNamesLower.includes(sp);
    }
    if (user?.role !== "buyer") return false;
    return (r.salesperson || "").trim().toLowerCase() ===
           (user?.name || "").trim().toLowerCase();
  };

  const [rows, setRows] = useState([]);
  const baseRowsRef = useRef(new Map());
  const savePromisesRef = useRef(new Map());
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  // 2026-02-27 · Per-row History button — opens the audit-log dialog scoped to
  // the clicked row's item_number.
  const [historyItem, setHistoryItem] = useState(null);

  // Search — when non-empty, drops the item_number filter and queries across
  // the whole /procurement-quotes collection (up to page_size=200).
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // debounced

  // Secondary (client-side) filter row — user spec 2026-02: price range +
  // date range only (removed factory/salesperson filters for a tighter UX).
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({ minPrice: "", maxPrice: "", dateFrom: "", dateTo: "" });

  // Track which rows are the freshly copied, still-editable ones so we render
  // <input> cells for them. Also remember pending edits per row id so blur can
  // send a full payload.
  const [editableIds, setEditableIds] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null);
  // 2026-06 · 其他明细 弹窗（与采购报价页一致）——报价单里的采购报价记录也能录明细。
  const [otherDetailsRow, setOtherDetailsRow] = useState(null);

  // 2026-06 · Search runs ONLY on Enter (or the search button) — live/debounced
  // search made the table flicker on every keystroke (user report).
  const runSearch = () => setSearchQuery(searchInput.trim());

  const loadRows = () => {
    setLoading(true);
    const params = { page_size: 200 };
    if (searchQuery) params.search = searchQuery;
    else if (buyerItemNumber) params.item_number = buyerItemNumber;
    api.get("/procurement-quotes", { params })
      .then(({ data }) => { setRows(data.items); baseRowsRef.current = new Map(data.items.map((row) => [row.id, row])); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setEditableIds(new Set());
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, buyerItemNumber, searchQuery]);

  // Apply the secondary client-side filter over the fetched rows.
  const filteredRows = useMemo(() => {
    const minP = parseFloat(filter.minPrice);
    const maxP = parseFloat(filter.maxPrice);
    const df = filter.dateFrom || "";
    const dt = filter.dateTo || "";
    return rows.filter((r) => {
      const price = num(r.price);
      if (!Number.isNaN(minP) && price < minP) return false;
      if (!Number.isNaN(maxP) && price > maxP) return false;
      // quote_date is stored as ISO string "YYYY-MM-DD" (see procurement_quotes.py);
      // lexicographic compare is exact for this format.
      const qd = (r.quote_date || "").slice(0, 10);
      if (df && qd && qd < df) return false;
      if (df && !qd) return false;
      if (dt && qd && qd > dt) return false;
      return true;
    });
  }, [rows, filter]);

  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredRows.forEach((r) => next.delete(r.id));
      else filteredRows.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggle = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleImport = async () => {
    setImporting(true);
    try {
      const picked = filteredRows.filter((r) => selected.has(r.id))
        .map((r) => toPriceRow(r, user?.name || user?.email || ""));
      await onImport(picked);
      onOpenChange(false);
    } finally {
      setImporting(false);
    }
  };

  // 2026-06 · 新增记录 — creates a blank WebApp row (source="web") for the
  // current 采购产品编号 and drops it at the top, inline-editable like a copy.
  const handleCreateBlank = async () => {
    if (!canCopy) return;
    setSavingId("__creating__");
    try {
      const payload = {
        item_number: buyerItemNumber || "(new)",
        name_cn: "(new)",
        size: "(new)",
        factory: "(new)",
        price: 0,
        quote_date: new Date().toISOString().slice(0, 10),
        salesperson: user?.name || "",
      };
      const { data } = await api.post("/procurement-quotes", payload);
      baseRowsRef.current.set(data.id, data);
      setRows((prev) => [data, ...prev]);
      setEditableIds((prev) => new Set(prev).add(data.id));
      toast.success(t("pq.createdOk"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("pq.saveFailed"));
    } finally {
      setSavingId(null);
    }
  };

  // Copy → creates a new WebApp-side procurement quote (source="web") based on
  // this row's data. The returned row is prepended and marked editable.
  const handleCopy = async (src) => {
    if (!canCopy) return;
    setSavingId("__copying__");
    try {
      // Missing required fields would 422 the API — fill with a placeholder so
      // the user can immediately edit inline. Empty required fields default to
      // the source row's value (already validated on the FM side).
      const payload = rowToPayload(src);
      // 2026-02 · Copies always take TODAY as the quote date — the whole point
      // of the copy is to reuse an old record as a template for a new quote,
      // so carrying the original date over is almost always wrong.
      payload.quote_date = new Date().toISOString().slice(0, 10);
      for (const k of REQUIRED_FIELDS) {
        if (!payload[k] && payload[k] !== 0) payload[k] = k === "price" ? 0 : "(copy)";
      }
      const { data } = await api.post("/procurement-quotes", payload);
      baseRowsRef.current.set(data.id, data);
      setRows((prev) => [data, ...prev]);
      setEditableIds((prev) => new Set(prev).add(data.id));
      toast.success(t("pq.copied"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("pq.copyFailed"));
    } finally {
      setSavingId(null);
    }
  };

  // 2026-02-27 · Dirty-row registry — set by ``updateCell`` on every
  // keystroke, cleared once ``saveRow`` succeeds. Used by
  // ``handleOpenChange`` below to force a synchronous flush when the user
  // closes the dialog (fixes "close list drops edits" bug).
  const dirtyRowsRef = useRef(new Set());

  // Called by every editable input on blur — persists the whole row via PUT
  // so a user leaving any cell auto-saves without needing a save button.
  // 2026-02-27 · Also mirrors the backend price formula so the ``价格`` cell
  // updates instantly when the user edits a formula input, instead of
  // waiting for the PUT round-trip.
  const saveRow = async (row) => {
    if (savePromisesRef.current.has(row.id)) {
      await savePromisesRef.current.get(row.id);
      const latest = rowsRef.current.find((item) => item.id === row.id);
      return latest ? saveRow(latest) : undefined;
    }
    const base = baseRowsRef.current.get(row.id) || row;
    const payload = pqChanges(row, base);
    if (!payload) { dirtyRowsRef.current.delete(row.id); return; }
    setSavingId(row.id);
    const promise = (async () => {
      try {
        const { data } = await api.put(`/procurement-quotes/${row.id}`, payload);
        baseRowsRef.current.set(row.id, data);
        const nextRows = rowsRef.current.map((r) => {
          if (r.id !== row.id) return r;
          const newer = pqChanges(r, row);
          if (!newer) { dirtyRowsRef.current.delete(row.id); return data; }
          const { base_values, ...draft } = newer;
          return { ...data, ...draft };
        });
        rowsRef.current = nextRows;
        setRows(nextRows);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("pq.saveFailed"));
        throw e;
      } finally {
        savePromisesRef.current.delete(row.id);
        setSavingId(null);
      }
    })();
    savePromisesRef.current.set(row.id, promise);
    await promise;
    if (dirtyRowsRef.current.has(row.id)) {
      const latest = rowsRef.current.find((item) => item.id === row.id);
      if (latest) return saveRow(latest);
    }
  };

  // 2026-06 · 保存其他明细：其他 = 明细合计（只读），并即时重算价格。
  const saveOtherDetails = async (row, details) => {
    const total = sumOtherDetails(details);
    const next = { ...row, others_details: details, others: details.length ? String(total) : row.others };
    next.price = computePqPrice(next);
    setRows((prev) => prev.map((r) => (r.id === row.id ? next : r)));
    try {
      const { data } = await api.put(`/procurement-quotes/${row.id}`, {
        others_details: details, base_values: { others_details: row.others_details ?? null },
      });
      baseRowsRef.current.set(row.id, data);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...data } : r)));
      toast.success(t("pq.autoSaved"));
    } catch (e) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("pq.saveFailed"));
    }
  };

  const updateCell = (id, key, value) => {    dirtyRowsRef.current.add(id);
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const next = { ...r, [key]: value };
      // Mirror backend recompute for immediate ``价格`` feedback — same
      // formula in JS as backend ``_compute_pq_price``.
      if (PRICE_FORMULA_INPUTS.has(key)) {
        next.price = computePqPrice(next);
      }
      return next;
    }));
  };

  // Track which row is currently being edited so we can flush a save when the
  // focus leaves the row entirely (not just moving between its cells).
  const activeRowRef = useRef(null);
  const handleRowFocus = (id) => {
    activeRowRef.current = id;
  };
  const handleRowBlur = (row) => {
    // setTimeout lets the browser move focus first — if the new focus target
    // is still inside this row we abort; otherwise we PUT.
    setTimeout(() => {
      const target = document.activeElement;
      const inSameRow = target?.closest?.(`[data-pq-row="${row.id}"]`);
      if (inSameRow) return;
      if (activeRowRef.current === row.id) {
        saveRow(row).catch(() => {});
        activeRowRef.current = null;
      }
    }, 40);
  };

  // 2026-02-27 · Flush any dirty rows the user has typed into BEFORE the
  // dialog unmounts, otherwise the input's ``onBlur`` (which normally
  // triggers ``saveRow``) never fires and the last edits are silently lost.
  const handleOpenChange = async (nextOpen) => {
    if (!nextOpen) {
      // Blur the currently-focused input so its edit lands in state.
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
      const dirtyIds = Array.from(dirtyRowsRef.current);
      if (dirtyIds.length > 0) {
        // Wait for pending saves and then flush any still-dirty rows.
        // Uses the freshest rows from state via a functional lookup below.
          try {
            await Promise.all(dirtyIds.map((id) => {
              const target = rows.find((r) => r.id === id);
              return target ? saveRow(target) : Promise.resolve();
            }));
          } catch { return; }
      }
    }
    onOpenChange(nextOpen);
  };

  const remarkCellClass = "max-w-[448px] truncate"; // ~40% wider than the previous 320px
  const importCount = Array.from(selected).filter((id) => filteredRows.some((r) => r.id === id)).length;

  // 2026-06 · Column layout (widths + visibility) — shared hook, see
  // hooks/useColumnLayout.js. Widths are measured once from the natural layout
  // so the default look is unchanged, then the table switches to fixed layout.
  const tableRef = useRef(null);
  const { widths: colW, setWidths: setColW, startResize, measure, resetAll: resetColumns } =
    useColumnLayout("pqHistoryColWidths");
  const { hidden: hiddenCols, toggle: toggleCol, showAll: showAllCols } =
    useHiddenColumns("pqHistoryHiddenCols");
  const measuredRef = useRef(false);

  useEffect(() => {
    if (!open) { measuredRef.current = false; return; }
    if (measuredRef.current) return;
    const raf = requestAnimationFrame(() => {
      if (measure(tableRef.current)) {
        measuredRef.current = true;
        // 2026-06 · 「其他」列多了明细按钮，给它留够宽度否则数字被挤掉。
        setColW((prev) => (Number(prev.others) >= 84 ? prev : { ...prev, others: 84 }));
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [open, rows.length, measure, setColW]);

  const widthsReady = Object.keys(colW).length > 0;

  const visibleColumns = COLUMNS.filter((c) => !hiddenCols.has(c.key));
  // Explicit total width — with table-layout:fixed a 100%-wide table would
  // redistribute leftover space and silently ignore the dragged widths.
  const totalColW = (canCopy ? (colW.__copy || 40) : 0) + (colW.__history || 40) + (colW.__check || 32)
    + visibleColumns.reduce((s, c) => s + (colW[c.key] || 120), 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw]" data-testid="pq-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-3 flex-wrap">
            <span>{t("pq.historyTitle")}</span>
            <span className="font-mono text-xs font-normal text-muted-foreground">
              {searchQuery ? `"${searchQuery}"` : buyerItemNumber} · {filteredRows.length} / {rows.length} {t("pq.records")}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">{t("pq.historyTitle")}</DialogDescription>
        </DialogHeader>

        {/* Search + secondary filter toggle */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="pq-history-toolbar">
          <div className="relative flex-1 min-w-[220px] max-w-[420px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
              placeholder={t("pq.searchPlaceholder")}
              className="h-8 text-xs pl-8 pr-8"
              data-testid="pq-history-search-input"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(""); setSearchQuery(""); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="pq-history-search-clear"
                aria-label="clear"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={runSearch}
            data-testid="pq-history-search-button" className="h-8 text-xs">
            <Search size={13} className="mr-1" /> {t("pq.searchButton")}
          </Button>
          <Button
            size="sm"
            variant={showFilter ? "default" : "outline"}
            onClick={() => setShowFilter((v) => !v)}
            data-testid="pq-history-filter-toggle"
            className="h-8 text-xs"
          >
            <FilterIcon size={13} className="mr-1" /> {t("pq.filter")}
          </Button>
          {canCopy && (
            <Button
              size="sm" variant="outline"
              onClick={handleCreateBlank}
              disabled={savingId === "__creating__"}
              data-testid="pq-history-new-button"
              className="h-8 text-xs"
            >
              <Plus size={13} className="mr-1" /> {t("pq.newRecord")}
            </Button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" data-testid="pq-history-columns-button" className="h-8 text-xs">
                <SlidersHorizontal size={13} className="mr-1" /> {t("pq.columns")}
                {hiddenCols.size > 0 && (
                  <span className="ml-1 text-[10px] font-mono text-brand">-{hiddenCols.size}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-2" data-testid="pq-history-columns-popover">
              <div className="flex items-center justify-between mb-1.5 px-1">
                <span className="text-xs font-semibold">{t("pq.columns")}</span>
                <Button size="sm" variant="ghost" onClick={showAllCols}
                  data-testid="pq-history-columns-show-all" className="h-6 text-[11px] px-2">
                  {t("pq.columnsShowAll")}
                </Button>
              </div>
              <div className="max-h-72 overflow-auto space-y-0.5">
                {COLUMNS.map((c) => (
                  <label key={c.key}
                    className="flex items-center gap-2 px-1 py-1 rounded hover:bg-secondary/60 cursor-pointer text-xs"
                    data-testid={`pq-history-col-toggle-${c.key}`}>
                    <Checkbox checked={!hiddenCols.has(c.key)} onCheckedChange={() => toggleCol(c.key)} />
                    <span className="truncate">{t(c.label)}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {widthsReady && (
            <Button size="sm" variant="ghost" onClick={resetColumns}
              data-testid="pq-history-reset-columns" className="h-8 text-xs text-muted-foreground">
              <Columns3 size={13} className="mr-1" /> {t("pq.resetColumns")}
            </Button>
          )}
          {loading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
        </div>

        {showFilter && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-2 rounded-md border border-border bg-secondary/30" data-testid="pq-history-filter-row">
            <Input placeholder={t("pq.minPrice")} type="number" value={filter.minPrice} onChange={(e) => setFilter((f) => ({ ...f, minPrice: e.target.value }))} className="h-8 text-xs" data-testid="pq-history-filter-min-price" />
            <Input placeholder={t("pq.maxPrice")} type="number" value={filter.maxPrice} onChange={(e) => setFilter((f) => ({ ...f, maxPrice: e.target.value }))} className="h-8 text-xs" data-testid="pq-history-filter-max-price" />
            <Input placeholder={t("pq.dateFrom")} type="date" value={filter.dateFrom} onChange={(e) => setFilter((f) => ({ ...f, dateFrom: e.target.value }))} className="h-8 text-xs" data-testid="pq-history-filter-date-from" />
            <Input placeholder={t("pq.dateTo")} type="date" value={filter.dateTo} onChange={(e) => setFilter((f) => ({ ...f, dateTo: e.target.value }))} className="h-8 text-xs" data-testid="pq-history-filter-date-to" />
            <Button variant="ghost" size="sm" onClick={() => setFilter({ minPrice: "", maxPrice: "", dateFrom: "", dateTo: "" })} className="h-8 text-xs justify-start" data-testid="pq-history-filter-reset">
              <X size={12} className="mr-1" /> {t("common.reset")}
            </Button>
          </div>
        )}

        <div className="border border-border rounded-md overflow-auto max-h-[60vh]">
          <table ref={tableRef}
            className="border-collapse text-xs whitespace-nowrap"
            style={widthsReady
              ? { tableLayout: "fixed", width: `${totalColW}px`, minWidth: "100%" }
              : { minWidth: "100%" }}
            data-testid="pq-history-table">
            {widthsReady && (
              <colgroup>
                {canCopy && <col style={{ width: colW.__copy || 40 }} />}
                <col style={{ width: colW.__history || 40 }} />
                <col style={{ width: colW.__check || 32 }} />
                {visibleColumns.map((c) => (
                  <col key={c.key} style={{ width: colW[c.key] || 120 }} />
                ))}
              </colgroup>
            )}
            <thead>
              <tr className="bg-secondary sticky top-0 z-10">
                {canCopy && <th data-col="__copy" className="border border-border px-2 py-1.5 bg-secondary w-10 text-center text-[10px] text-muted-foreground font-semibold uppercase">{t("pq.copyColHeader")}</th>}
                <th data-col="__history" className="border border-border px-2 py-1.5 bg-secondary w-10 text-center text-[10px] text-muted-foreground font-semibold uppercase">{t("pq.history")}</th>
                <th data-col="__check" className="border border-border px-2 py-1.5 bg-secondary w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="pq-history-select-all" />
                </th>
                {visibleColumns.map((c) => (
                  <th key={c.key} data-col={c.key}
                    className="relative border border-border px-2 py-1.5 text-left font-semibold uppercase text-[10px] tracking-wide text-muted-foreground bg-secondary select-none">
                    <span className="block truncate">{t(c.label)}</span>
                    <span
                      onMouseDown={startResize(c.key, 48)}
                      onDoubleClick={() => setColW((prev) => ({ ...prev, [c.key]: 160 }))}
                      title={t("pq.resizeHint")}
                      data-testid={`pq-history-resize-${c.key}`}
                      className="group absolute top-0 -right-[8px] z-20 h-full w-5 cursor-col-resize flex items-center justify-center"
                    >
                      <span className="block h-full w-[3px] bg-border group-hover:bg-brand group-active:bg-brand transition-colors" />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + 2 + (canCopy ? 1 : 0)} className="text-center py-8 text-muted-foreground border border-border" data-testid="pq-history-empty">
                    {t("pq.historyEmpty")}
                  </td>
                </tr>
              )}
              {filteredRows.map((r) => {
                const isEditable = editableIds.has(r.id) || canEditRow(r);
                const isSaving = savingId === r.id;
                return (
                  <tr
                    key={r.id}
                    className={`hover:bg-secondary/40 ${isEditable ? "bg-sky-50" : ""}`}
                    data-testid={`pq-history-row-${r.fm_record_id || r.id}`}
                    data-pq-row={r.id}
                    onFocus={() => handleRowFocus(r.id)}
                    onBlur={() => isEditable && handleRowBlur(r)}
                  >
                    {canCopy && (
                      <td className="border border-border px-1 py-1 text-center">
                        <button
                          onClick={() => handleCopy(r)}
                          disabled={savingId === "__copying__"}
                          className="p-1 rounded hover:bg-brand/10 text-brand transition-colors disabled:opacity-50"
                          title={t("pq.copyRow")}
                          data-testid={`pq-history-copy-${r.id}`}
                        >
                          <Copy size={12} />
                        </button>
                      </td>
                    )}
                    <td className="border border-border px-1 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => setHistoryItem(r.item_number)}
                        disabled={!r.item_number}
                        className="p-1 rounded hover:bg-brand/10 text-brand transition-colors disabled:opacity-30"
                        title={t("pq.history")}
                        data-testid={`pq-history-open-${r.id}`}
                      >
                        <HistoryIcon size={12} />
                      </button>
                    </td>
                    <td className="border border-border px-2 py-1 text-center">
                      <Checkbox
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggle(r.id)}
                        data-testid={`pq-history-check-${r.fm_record_id || r.id}`}
                      />
                    </td>
                    {visibleColumns.map((c) => {
                      const isRemark = c.key === "remark";
                      const baseClass = `border border-border px-2 py-1 ${c.mono || c.num ? "font-mono" : ""} ${c.num ? "text-right" : ""} ${isRemark ? remarkCellClass : c.wide ? "max-w-[320px] truncate" : "max-w-[200px] truncate"}`;
                      if (!isEditable) {
                        return (
                          <td key={c.key} className={baseClass} title={String(r[c.key] ?? "")}>
                            {cellValue(r, c)}
                          </td>
                        );
                      }
                      if (c.key === "others") {
                        // 2026-06 · 其他 单元格 = 数值 + 「其他明细」按钮（与采购报价页一致）。
                        const locked = (r.others_details || []).length > 0;
                        return (
                          <td key={c.key} className="border border-border p-0.5">
                            <div className="flex items-center gap-0.5">
                              <input
                                value={r.others ?? ""}
                                readOnly={locked}
                                onChange={(e) => updateCell(r.id, "others", e.target.value)}
                                onBlur={() => { if (!locked) saveRow(rows.find((x) => x.id === r.id) || r).catch(() => {}); }}
                                title={locked ? t("pq.othersLockedHint") : ""}
                                data-testid={`pq-history-input-${r.id}-others`}
                                className={`flex-1 min-w-0 px-1 py-1 text-xs text-right font-mono border-none rounded-sm focus:outline-none focus:ring-1 focus:ring-brand/60 ${locked ? "bg-secondary/60 text-muted-foreground" : "bg-transparent"}`}
                                disabled={isSaving}
                              />
                              <button type="button" onClick={() => setOtherDetailsRow(r)}
                                title={t("pq.otherDetails")}
                                data-testid={`pq-history-other-details-${r.id}`}
                                className="shrink-0 p-0.5 rounded text-brand hover:bg-brand/10 transition-colors relative">
                                <ListPlus size={12} />
                                {locked && <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-brand" />}
                              </button>
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} className={`border border-border p-0.5 ${isRemark ? "" : ""}`}>
                          <input
                            value={r[c.key] ?? ""}
                            onChange={(e) => updateCell(r.id, c.key, NUMERIC_FIELDS.has(c.key) ? e.target.value : e.target.value)}
                            className={`w-full px-1.5 py-1 text-xs bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-brand/60 rounded-sm ${c.num ? "text-right font-mono" : ""} ${isRemark ? "min-w-[400px]" : ""}`}
                            data-testid={`pq-history-input-${r.id}-${c.key}`}
                            disabled={isSaving}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <span className="font-mono text-xs text-muted-foreground self-center mr-auto" data-testid="pq-history-selected-count">
            {importCount} / {filteredRows.length}
          </span>
          <Button variant="outline" onClick={async () => {
            await handleOpenChange(false);
          }} className="rounded-md" data-testid="pq-history-close-button">
            {t("common.cancel")}
          </Button>
          {onImport && (
          <Button
            onClick={handleImport}
            disabled={importCount === 0 || importing}
            data-testid="pq-history-import-button"
            className="rounded-md bg-brand hover:bg-brand-hover transition-colors"
          >
            <Download size={14} /> {t("pq.importSelected")} ({importCount})
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
      {/* 2026-02-27 · Nested modification-history modal opened via the
          per-row History button. */}
      <PqHistoryDialog
        itemNumber={historyItem}
        open={Boolean(historyItem)}
        onOpenChange={(o) => { if (!o) setHistoryItem(null); }}
      />
      {/* 2026-06 · 其他明细录入窗口（报价单 → 采购报价记录） */}
      <OtherCostDetailsDialog
        open={Boolean(otherDetailsRow)}
        onOpenChange={(o) => {
          if (!o && otherDetailsRow) {
            const original = rows.find((x) => x.id === otherDetailsRow.id) || otherDetailsRow;
            const details = otherDetailsRow.others_details || [];
            if (JSON.stringify(original.others_details || []) !== JSON.stringify(details)) {
              saveOtherDetails(original, details);
            }
            setOtherDetailsRow(null);
          }
        }}
        rows={otherDetailsRow?.others_details || []}
        onChange={(details) => setOtherDetailsRow((r) => (r ? { ...r, others_details: details } : r))}
      />
    </Dialog>
  );
}
